import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useRpcData } from "@redotech/react-util/rpc-data";
import { RedoInventoryRpcClientContext } from "@redotech/redo-merchant-app-common/inventory-rpc-client-provider";
import { folderTypes } from "@redotech/redo-model/views/folders";
import { ShipmentView } from "@redotech/redo-model/views/views";
import {
  ReactNode,
  createContext,
  memo,
  useEffect,
  useMemo,
  useState,
} from "react";
import { CreateShipmentViewFolderModal } from "./create-shipment-view-folder-modal";
import { EditShipmentViewFolderModal } from "./edit-shipment-view-folder-modal";

export interface ShipmentViewsData {
  openCreateFolderModal: () => void;
  openEditFolderModal: (folderId: string) => void;
  handleDeleteFolder: (folderId: string) => void;
  updateOrdering: (
    updatedViews: ShipmentView[],
    updatedFolders: folderTypes["shipments"][],
  ) => void;
  views?: ShipmentView[];
  folders?: folderTypes["shipments"][];
  reloadViews: () => void;
}

export const ShipmentViewsContext = createContext<
  ShipmentViewsData | undefined
>(undefined);

export const ShipmentViewsProvider = memo(function ShipmentViewsProvider({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const client = useRequiredContext(RedoInventoryRpcClientContext);

  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showEditFolderModal, setShowEditFolderModal] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<
    folderTypes["shipments"] | null
  >(null);

  const { mutations, ...viewsLoad } = useRpcData(
    client,
    "getShipmentViewsAndFolders",
    {
      input: null,
      mutations: [
        "createShipmentViewFolder",
        "updateShipmentViewFolder",
        "deleteShipmentViewFolder",
        "updateShipmentViewOrdering",
      ],
    },
  );

  // Store the state separately from the load so we can do optimistic updates
  const [views, setViews] = useState<ShipmentView[]>([]);
  const [folders, setFolders] = useState<folderTypes["shipments"][]>([]);
  useEffect(() => {
    if (viewsLoad.value) {
      setViews([...viewsLoad.value.views]);
      setFolders([...viewsLoad.value.folders]);
    }
  }, [viewsLoad.value]);

  const handleCreateFolder = useHandler(async (name: string) => {
    await mutations.createShipmentViewFolder({ name });
  });

  const handleEditFolder = useHandler(
    async (folderId: string, folderName: string) => {
      await mutations.updateShipmentViewFolder({
        folderId,
        name: folderName,
      });
    },
  );

  const handleDeleteFolder = useHandler(async (folderId: string) => {
    await mutations.deleteShipmentViewFolder({ folderId });
  });

  const updateOrdering = useHandler(
    async (
      updatedViews: ShipmentView[],
      updatedFolders: folderTypes["shipments"][],
    ) => {
      setViews(updatedViews);
      setFolders(updatedFolders);

      try {
        await mutations.updateShipmentViewOrdering({
          views: updatedViews,
          folders: updatedFolders,
        });
      } catch (error) {
        console.error("Error saving view and folder ordering", error);
      }
    },
  );

  const openCreateFolderModal = useHandler(() => {
    setShowCreateFolderModal(true);
  });

  const openEditFolderModal = useHandler((folderId: string) => {
    const folder = folders.find((f) => f._id === folderId);
    if (folder) {
      setSelectedFolder(folder);
      setShowEditFolderModal(true);
    }
  });

  const reservedFolderNames = useMemo(() => {
    const names = new Set(folders.map((f) => f.name));
    if (selectedFolder) {
      names.delete(selectedFolder.name);
    }
    return names;
  }, [folders, selectedFolder]);

  const contextValue: ShipmentViewsData = {
    openCreateFolderModal,
    openEditFolderModal,
    handleDeleteFolder,
    updateOrdering,
    views,
    folders,
    reloadViews: () => viewsLoad.refresh(true),
  };

  return (
    <ShipmentViewsContext.Provider value={contextValue}>
      {children}
      <CreateShipmentViewFolderModal
        isOpen={showCreateFolderModal}
        onClose={() => setShowCreateFolderModal(false)}
        onSubmit={handleCreateFolder}
        reservedFolderNames={reservedFolderNames}
      />
      {selectedFolder && (
        <EditShipmentViewFolderModal
          deleteFolder={handleDeleteFolder}
          isOpen={showEditFolderModal}
          onClose={() => setShowEditFolderModal(false)}
          onSubmit={handleEditFolder}
          reservedFolderNames={reservedFolderNames}
          selectedFolder={selectedFolder}
        />
      )}
    </ShipmentViewsContext.Provider>
  );
});
